import {
  CuripodDecendant,
  CuripodElement,
  isCuripodElement,
  isCuripodMathBlockElement,
  isCuripodMathInlineElement,
  parseStringToSlateObject,
} from '../components/SlideComponents/slateUtil';

export interface WordOffset {
  word: string;
  /** Start character index in the plain text */
  startChar: number;
  /** End character index (exclusive) in the plain text */
  endChar: number;
  /**
   * Full Slate path to the node this word should highlight — the text leaf for
   * spoken text, or the math element itself for voiced math. Carrying the whole
   * path (not a fixed element/child pair) lets highlighting follow text at any
   * nesting depth, e.g. numbered-list → list-item → paragraph → text.
   */
  path: number[];
  /** Character offset within the Slate text leaf where this word starts */
  slateCharStart: number;
  /** Character offset within the Slate text leaf where this word ends (exclusive) */
  slateCharEnd: number;
}

export interface WordTiming {
  word: string;
  startTime: number;
  endTime: number;
  /** Full Slate path to the node to highlight (see WordOffset.path). */
  path: number[];
  slateCharStart: number;
  slateCharEnd: number;
}

export interface ElevenLabsAlignment {
  characters: string[];
  character_start_times_seconds: number[];
  character_end_times_seconds: number[];
}

const hasStringText = (node: CuripodDecendant): node is { text: string } & object =>
  typeof (node as { text?: unknown }).text === 'string';

/**
 * Best-effort conversion of a LaTeX math value into something ElevenLabs can voice.
 * Returns null when the input still contains unrecognised LaTeX after substitution
 * (backslash commands, residual ^ / _ / braces) — the caller treats null as "skip",
 * so unintelligible expressions stay silent rather than reading out raw LaTeX.
 *
 * Dollar signs are always stripped from the output, so the TTS endpoint never
 * receives `$...$` wrappers.
 */
const mathToSpeech = (value: string): string | null => {
  let s = value;

  // Multi-arg structural commands first so their inner contents are exposed
  // to the symbol pass below. Iterate to handle simple nesting.
  for (let i = 0; i < 5; i++) {
    const before = s;
    s = s.replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, ' $1 over $2 ');
    s = s.replace(/\\sqrt\s*\{([^{}]+)\}/g, ' square root of $1 ');
    if (s === before) break;
  }

  // Powers — specific cases before the generic fallback. Trailing spaces in
  // the replacements compensate for the trailing `\s*` in each pattern, which
  // would otherwise swallow the separator before the next token.
  s = s.replace(/\^\s*\{?\s*\\circ\s*\}?/g, ' degrees ');
  s = s.replace(/\^\s*\{?\s*2\s*\}?/g, ' squared ');
  s = s.replace(/\^\s*\{?\s*3\s*\}?/g, ' cubed ');
  s = s.replace(/\^\s*\{([^{}]+)\}/g, ' to the $1 ');
  s = s.replace(/\^([A-Za-z0-9])/g, ' to the $1 ');

  // Subscripts after a letter ("H_2O" → "H sub 2 O").
  s = s.replace(/([A-Za-z])_\s*\{([^{}]+)\}/g, '$1 sub $2 ');
  s = s.replace(/([A-Za-z])_([A-Za-z0-9])/g, '$1 sub $2 ');

  // Common symbols and operators.
  s = s.replace(/\\degrees?\b/g, ' degrees');
  s = s.replace(/\\textdegree\b/g, ' degrees');
  s = s.replace(/\\pi\b/g, ' pi ');
  s = s.replace(/\\times\b/g, ' times ');
  s = s.replace(/\\cdot\b/g, ' times ');
  s = s.replace(/\\div\b/g, ' divided by ');
  s = s.replace(/\\pm\b/g, ' plus or minus ');
  s = s.replace(/\\leq?\b/g, ' less than or equal to ');
  s = s.replace(/\\geq?\b/g, ' greater than or equal to ');
  s = s.replace(/\\neq?\b/g, ' not equal to ');
  s = s.replace(/\\approx\b/g, ' approximately ');
  s = s.replace(/\\infty\b/g, ' infinity ');

  // Whitespace macros and tildes.
  s = s.replace(/\\[,;:!]/g, ' ');
  s = s.replace(/~/g, ' ');

  // Strip leftover decorative braces and stray dollar signs — we never want
  // either reaching the TTS endpoint.
  s = s.replace(/[{}$]/g, '');

  // Temperature post-pass — only fires when a preceding "degrees" was produced
  // by the ^\circ conversion, so standalone C/F variables (e.g. "F = ma") are
  // left alone.
  s = s.replace(/\bdegrees\s+C\b/g, ' degrees Celsius ');
  s = s.replace(/\bdegrees\s+F\b/g, ' degrees Fahrenheit ');

  // Normalise whitespace.
  s = s.replace(/\s+/g, ' ').trim();

  // Any remaining LaTeX residue means we couldn't safely voice it.
  if (s.length === 0) return null;
  if (/[\\^_]/.test(s)) return null;

  return s;
};

/**
 * Render a Slate child as the plain-text fragment it should contribute to the TTS
 * payload. Math elements are best-effort converted to spoken English via
 * mathToSpeech; expressions we can't translate return null and are skipped, so
 * audio is silent across that span rather than reading raw LaTeX or dollar signs.
 */
const getRenderableText = (node: CuripodDecendant): string | null => {
  if (hasStringText(node)) return node.text;
  if (isCuripodMathInlineElement(node)) return mathToSpeech(node.value);
  if (isCuripodMathBlockElement(node)) return mathToSpeech(node.value);
  return null;
};

/**
 * Extract plain text from Slate JSON content, tracking each word's position
 * back to its Slate path for highlighting.
 */
export function extractPlainTextWithOffsets(
  slateContent: string,
): {
  plainText: string;
  wordMap: WordOffset[];
} {
  const elements = parseStringToSlateObject(slateContent);
  let plainText = '';
  const wordMap: WordOffset[] = [];
  // Tracks whether we've emitted anything yet so we can insert the
  // inter-element space lazily, only when a later element actually contributes.
  let hasEmittedAny = false;

  const pushWords = (text: string, path: number[], baseOffset: number) => {
    const wordRegex = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = wordRegex.exec(text)) !== null) {
      wordMap.push({
        word: match[0],
        startChar: baseOffset + match.index,
        endChar: baseOffset + match.index + match[0].length,
        path,
        slateCharStart: match.index,
        slateCharEnd: match.index + match[0].length,
      });
    }
  };

  for (let elementIdx = 0; elementIdx < elements.length; elementIdx++) {
    const element = elements[elementIdx];

    if (isCuripodMathBlockElement(element)) {
      const text = mathToSpeech(element.value);
      if (!text) continue;
      if (hasEmittedAny) plainText += ' ';
      // Highlight the math-block element itself (path [elementIdx]); renderElement
      // matches on the element's path, so decorate() never touches its empty text
      // leaf at [elementIdx, 0] (a different, longer path) and can't crash Slate.
      pushWords(text, [elementIdx], plainText.length);
      plainText += text;
      hasEmittedAny = true;
      continue;
    }

    const children = getSpeakableChildren(element, [elementIdx]);
    let elementHasEmitted = false;
    let lastBlockIndex = -1;

    for (const { node, path, blockIndex } of children) {
      const text = getRenderableText(node);
      if (!text) continue;

      // Insert a single separator lazily before the first contributing leaf of
      // each new block (paragraph / list item), and before the first leaf of
      // this element. Lazy placement means empty or untranslatable-math blocks
      // never leave a stray or trailing space. Inline leaves within one block
      // share a blockIndex, so they stay contiguous (their spacing is already
      // baked into the text leaves).
      const isNewBlock = blockIndex !== lastBlockIndex;
      if (isNewBlock && (elementHasEmitted || hasEmittedAny)) plainText += ' ';
      lastBlockIndex = blockIndex;

      // Push words for all voiced nodes. For inline math, `path` is the math
      // element's own path — decorate()'s `'text' in node` guard means it never
      // matches the element node itself, so highlighting is done in renderElement.
      pushWords(text, path, plainText.length);

      plainText += text;
      elementHasEmitted = true;
    }

    if (elementHasEmitted) hasEmittedAny = true;
  }

  return { plainText, wordMap };
}

interface SpeakableLeaf {
  node: CuripodDecendant;
  /**
   * Full Slate path to the node (text leaf, or math element for voiced math),
   * including the top-level element index. Lets highlighting follow text at any
   * nesting depth.
   */
  path: number[];
  /**
   * Monotonically increasing per leaf-bearing block. Used by the caller to
   * insert a single space between sibling blocks (paragraphs, list items) so
   * words don't slur together — inline leaves within one block share an index.
   */
  blockIndex: number;
}

/**
 * Flatten a Slate element into its speakable leaves (text + math-inline),
 * recursing through block containers (lists, list-items, paragraphs, and any
 * nesting thereof) down to the lowest element that directly holds leaves.
 * Everything else (images, prompt placeholders, unknown types) is skipped.
 *
 * `basePath` is the path of `element` itself (e.g. `[elementIdx]`); each returned
 * leaf's `path` extends it with the indices walked to reach that leaf, so the
 * viewer can highlight via exact path equality regardless of depth.
 *
 * A "block" is the lowest element that directly contains text/math-inline leaves.
 */
function getSpeakableChildren(
  element: CuripodElement,
  basePath: number[],
): SpeakableLeaf[] {
  const result: SpeakableLeaf[] = [];
  // Boxed so the recursion shares a single monotonically increasing counter.
  const block = { next: 0 };

  const collect = (el: CuripodElement, path: number[]) => {
    if (!Array.isArray(el.children)) return;

    const directLeaves: { node: CuripodDecendant; idx: number }[] = [];
    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i];
      if (hasStringText(child) || isCuripodMathInlineElement(child)) {
        directLeaves.push({ node: child, idx: i });
      }
    }

    if (directLeaves.length > 0) {
      // Leaf-bearing block: emit its inline leaves under one block index. Any
      // non-leaf children mixed in (e.g. images) are skipped by the filter above.
      const blockIndex = block.next++;
      for (const { node, idx } of directLeaves) {
        result.push({ node, path: [...path, idx], blockIndex });
      }
      return;
    }

    // Container block: recurse into child elements so each leaf-bearing
    // descendant becomes its own block.
    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i];
      if (isCuripodElement(child) && Array.isArray((child as CuripodElement).children)) {
        collect(child as CuripodElement, [...path, i]);
      }
    }
  };

  collect(element, basePath);
  return result;
}

/**
 * Map ElevenLabs character-level alignment data onto our word offsets
 * to produce word-level timings.
 */
export function buildWordTimings(
  alignment: ElevenLabsAlignment,
  wordMap: WordOffset[],
): WordTiming[] {
  if (!alignment || wordMap.length === 0) return [];

  const timings: WordTiming[] = [];

  for (const word of wordMap) {
    // Find the character timing for the start of this word
    const startCharIdx = word.startChar;
    const endCharIdx = word.endChar - 1; // Last character of the word

    // Clamp to alignment array bounds
    const safeStart = Math.min(startCharIdx, alignment.characters.length - 1);
    const safeEnd = Math.min(endCharIdx, alignment.characters.length - 1);

    if (safeStart < 0 || safeEnd < 0) continue;

    timings.push({
      word: word.word,
      startTime: alignment.character_start_times_seconds[safeStart] ?? 0,
      endTime: alignment.character_end_times_seconds[safeEnd] ?? 0,
      path: word.path,
      slateCharStart: word.slateCharStart,
      slateCharEnd: word.slateCharEnd,
    });
  }

  return timings;
}
